import debugGameA from './debugGameA.json';
import debugGameB from './debugGameB.json';
import debugGameC from './debugGameC.json';
import debugGameD from './debugGameD.json';
import debugGameE from './debugGameE.json';
import debugGameF from './debugGameF.json';
import debugGameG from './debugGameG.json';
import debugGameH from './debugGameH.json';
import debugGameI from './debugGameI.json';
import type { GameSnapshot, GameState } from './dtos';
import { GameStatus } from './dtos';
import Turfmaster from './tmmodel';

export type SnapshotRestoreVariant = 0 | 1 | 2;

export type UserHorseRecord = {
  horseId: string;
  name?: string;
  color?: number;
  jockeyName?: string;
};

type HorseLike = {
  playerid: number;
  horseid?: string;
  name?: string;
  color?: number;
  jockeyName?: string;
};

export type InnerGameState = {
  currentState?: number;
  players: Array<{ userid?: string; type?: number; [key: string]: unknown }>;
  horses: HorseLike[];
  [key: string]: unknown;
};

const DEBUG_GAME_TEMPLATES: Record<string, GameState> = {
  debugGameA: debugGameA as unknown as GameState,
  debugGameB: debugGameB as unknown as GameState,
  debugGameC: debugGameC as unknown as GameState,
  debugGameD: debugGameD as unknown as GameState,
  debugGameE: debugGameE as unknown as GameState,
  debugGameF: debugGameF as unknown as GameState,
  debugGameG: debugGameG as unknown as GameState,
  debugGameH: debugGameH as unknown as GameState,
  debugGameI: debugGameI as unknown as GameState,
};

export const DEBUG_GAME_TEMPLATE_NAMES = Object.keys(DEBUG_GAME_TEMPLATES);

export function getDebugGameTemplateByName(gameName: string): GameState | null {
  return DEBUG_GAME_TEMPLATES[gameName] ?? null;
}

/** Parse `snapshot_<snapshotId>_<variant>`; snapshotId may contain underscores. */
export function parseSnapshotGameName(
  gameName: string,
): { snapshotId: string; variant: SnapshotRestoreVariant } | null {
  const match = /^snapshot_(.+)_([012])$/.exec(gameName);
  if (!match) return null;
  return {
    snapshotId: match[1],
    variant: Number(match[2]) as SnapshotRestoreVariant,
  };
}

export function countHorsesForPlayer(horses: HorseLike[], playerid: number): number {
  return horses.filter((h) => h.playerid === playerid).length;
}

export function countHumanHorses(horses: HorseLike[]): number {
  return countHorsesForPlayer(horses, 0);
}

export function applyUserHorsesForPlayerId(
  innerState: InnerGameState,
  playerid: number,
  userHorses: UserHorseRecord[],
): { ok: true } | { ok: false; error: string } {
  const required = countHorsesForPlayer(innerState.horses, playerid);
  if (required === 0) {
    return { ok: true };
  }
  if (userHorses.length < required) {
    return {
      ok: false,
      error: `error while restoring snapshot: need ${required} horses, have ${userHorses.length}`,
    };
  }

  let userHorseIndex = 0;
  innerState.horses.forEach((h) => {
    if (h.playerid === playerid) {
      const realHorse = userHorses[userHorseIndex];
      h.horseid = realHorse.horseId;
      h.name = realHorse.name;
      h.color = realHorse.color;
      h.jockeyName = realHorse.jockeyName;
      userHorseIndex++;
    }
  });
  return { ok: true };
}

export function applyUserHorsesToInnerState(
  innerState: InnerGameState,
  userHorses: UserHorseRecord[],
): { ok: true } | { ok: false; error: string } {
  return applyUserHorsesForPlayerId(innerState, 0, userHorses);
}

export function prepareDebugGameInstance(
  template: GameState,
  gameId: string,
  userId: string,
  userHorses: UserHorseRecord[],
): { ok: true; instance: GameState } | { ok: false; error: string } {
  const instance = JSON.parse(JSON.stringify(template)) as GameState;
  instance.gameId = gameId;
  instance.state.players[0].userid = userId;
  instance.visibility = instance.visibility ?? 'private';
  instance.allowSpectators = instance.allowSpectators ?? false;
  instance.chatVisibility = instance.chatVisibility ?? 'playersonly';

  const horseResult = applyUserHorsesToInnerState(instance.state as InnerGameState, userHorses);
  if (!horseResult.ok) {
    return horseResult;
  }
  return { ok: true, instance };
}

export function resolveSnapshotInnerState(
  snapshot: GameSnapshot,
  variant: SnapshotRestoreVariant,
): InnerGameState {
  if (variant === 1 && snapshot.gameState.statePrev !== undefined) {
    return JSON.parse(JSON.stringify(snapshot.gameState.statePrev)) as InnerGameState;
  }
  if (variant === 2 && snapshot.gameState.statePrevPrev !== undefined) {
    return JSON.parse(JSON.stringify(snapshot.gameState.statePrevPrev)) as InnerGameState;
  }
  return JSON.parse(JSON.stringify(snapshot.gameState.state)) as InnerGameState;
}

export function prepareSnapshotGameInstance(
  snapshot: GameSnapshot,
  gameId: string,
  userId: string,
  variant: SnapshotRestoreVariant,
  userHorses: UserHorseRecord[],
): { ok: true; innerState: InnerGameState; envelope: GameState } | { ok: false; error: string } {
  const innerState = resolveSnapshotInnerState(snapshot, variant);

  innerState.players[0].userid = userId;
  for (let p = 1; p < innerState.players.length; p++) {
    innerState.players[p].type = Turfmaster.playerTypes.COMPUTER;
  }

  const horseResult = applyUserHorsesToInnerState(innerState, userHorses);
  if (!horseResult.ok) {
    return horseResult;
  }

  const envelope: GameState = {
    gameId,
    timeCreated: snapshot.gameState.timeCreated ?? new Date().toISOString(),
    gameType: snapshot.gameState.gameType,
    boardId: snapshot.gameState.boardId,
    status: snapshot.gameState.status ?? GameStatus.Running,
    eventLog: snapshot.gameState.eventLog ?? [],
    lastRemovedCards: snapshot.gameState.lastRemovedCards ?? [],
    state: innerState as GameState['state'],
    visibility: snapshot.gameState.visibility ?? 'private',
    allowSpectators: snapshot.gameState.allowSpectators ?? false,
    chatVisibility: snapshot.gameState.chatVisibility ?? 'playersonly',
  };

  return { ok: true, innerState, envelope };
}
